import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Storage as DatabaseIcon,
  Refresh as RefreshIcon,
  PlayArrow as RunIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Settings as ETLIcon
} from '@mui/icons-material';

interface ETLRun {
  batch_id: string;
  sync_date: string;
  sync_type: string;
  status: string;
  started_at: string;
  completed_at: string;
  duration_seconds: number;
  records_processed: number;
  schools_updated: number;
  errors_count: number;
}

interface ZoneStats {
  zone_code: string;
  zone_name: string;
  schools_count: number;
}

interface LevelStats {
  school_level_code: string;
  school_level_name: string;
  schools_count: number;
}

interface DataWarehouseStats {
  total_schools: number;
  schools_by_zone: ZoneStats[];
  schools_by_level: LevelStats[];
  total_daily_facts: number;
  latest_date: string;
  earliest_date: string;
}

interface StatusData {
  etl_history: ETLRun[];
  data_warehouse_stats: DataWarehouseStats;
  last_updated: string;
}

const DataWarehouseStatus: React.FC = () => {
  const [statusData, setStatusData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [etlDialogOpen, setEtlDialogOpen] = useState(false);
  const [etlLoading, setEtlLoading] = useState(false);
  const [etlDate, setEtlDate] = useState(new Date().toISOString().split('T')[0]);
  const [fullRefresh, setFullRefresh] = useState(false);

  useEffect(() => {
    fetchDWStatus();
  }, []);

  const fetchDWStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const { attendanceAPI } = await import('../../services/attendanceApi');
      const response = await attendanceAPI.getDWStatus();

      if (response.data.status === 'success') {
        setStatusData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load DW status');
      }
    } catch (err: any) {
      console.error('DW status error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load DW status');
    } finally {
      setLoading(false);
    }
  };

  const triggerETL = async () => {
    try {
      setEtlLoading(true);
      setError(null);

      const { attendanceAPI } = await import('../../services/attendanceApi');
      const response = await attendanceAPI.triggerETL({
        date: etlDate,
        full_refresh: fullRefresh,
        force: fullRefresh, // Full refresh automatically forces override
      });

      if (response.data.status === 'success') {
        setEtlDialogOpen(false);
        alert(response.data.message || 'ETL process started successfully!');
        // Refresh status after successful ETL
        setTimeout(() => fetchDWStatus(), 2000);
      } else {
        throw new Error(response.data.message || 'Failed to trigger ETL process');
      }
    } catch (err: any) {
      console.error('ETL trigger error:', err);
      alert(`ETL failed: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    } finally {
      setEtlLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'running':
        return <CircularProgress size={20} />;
      default:
        return <WarningIcon color="warning" />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'info';
      default:
        return 'warning';
    }
  };

  const formatDuration = (seconds: number): string => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!statusData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No data warehouse status available
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          <DatabaseIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Data Warehouse Status
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDWStatus}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<RunIcon />}
            onClick={() => setEtlDialogOpen(true)}
          >
            Run ETL
          </Button>
        </Box>
      </Box>

      {/* Data Warehouse Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">Total Schools</Typography>
              <Typography variant="h3">{statusData.data_warehouse_stats.total_schools}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">Daily Facts</Typography>
              <Typography variant="h3">{statusData.data_warehouse_stats.total_daily_facts?.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">Latest Date</Typography>
              <Typography variant="h6">{statusData.data_warehouse_stats.latest_date || 'N/A'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">Last Updated</Typography>
              <Typography variant="h6">
                {statusData.last_updated ? new Date(statusData.last_updated).toLocaleDateString() : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Schools by Zone and Level */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Schools by Zone</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Zone</TableCell>
                      <TableCell align="right">Schools Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statusData.data_warehouse_stats.schools_by_zone.map((zone, index) => (
                      <TableRow key={index}>
                        <TableCell>{zone.zone_name}</TableCell>
                        <TableCell align="right">{zone.schools_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Schools by Level</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>School Level</TableCell>
                      <TableCell align="right">Schools Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statusData.data_warehouse_stats.schools_by_level.map((level, index) => (
                      <TableRow key={index}>
                        <TableCell>{level.school_level_name}</TableCell>
                        <TableCell align="right">{level.schools_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ETL History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <ETLIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Recent ETL Runs
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Batch ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="right">Records</TableCell>
                  <TableCell align="right">Schools</TableCell>
                  <TableCell align="right">Errors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statusData.etl_history.map((run, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getStatusIcon(run.status)}
                        <Chip
                          label={run.status}
                          color={getStatusColor(run.status)}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {run.batch_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{run.sync_date}</TableCell>
                    <TableCell>
                      <Chip label={run.sync_type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {new Date(run.started_at).toLocaleString()}
                    </TableCell>
                    <TableCell>{formatDuration(run.duration_seconds)}</TableCell>
                    <TableCell align="right">{run.records_processed?.toLocaleString()}</TableCell>
                    <TableCell align="right">{run.schools_updated}</TableCell>
                    <TableCell align="right">
                      {run.errors_count > 0 ? (
                        <Chip label={run.errors_count} color="error" size="small" />
                      ) : (
                        '0'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ETL Trigger Dialog */}
      <Dialog open={etlDialogOpen} onClose={() => setEtlDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Trigger ETL Process</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={etlDate}
              onChange={(e) => setEtlDate(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={fullRefresh}
                  onChange={(e) => setFullRefresh(e.target.checked)}
                />
              }
              label="Full Refresh (rebuilds dimensions and recent data)"
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              {fullRefresh
                ? 'Full refresh will update all dimensions and rebuild recent facts. This may take several minutes.'
                : 'Standard ETL will process attendance data for the specified date only.'
              }
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEtlDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={triggerETL}
            variant="contained"
            disabled={etlLoading}
            startIcon={etlLoading ? <CircularProgress size={16} /> : <RunIcon />}
          >
            {etlLoading ? 'Running...' : 'Run ETL'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataWarehouseStatus;